import React, { forwardRef } from 'react'
import { Item } from '@radix-ui/react-dropdown-menu'

import {
  buttonStyles,
  iconStyles,
  itemStyles,
  linkStyles,
  subtitleStyles,
  titleStyles,
} from './styles'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { Link, LinkType } from '@sidecar-components/atoms/Link'
import { MenuItemProps } from './MenuItem.types'

/**
 * Creates a menu item with support for icons and sub-titles. Uses 2 separate
 * elements to keep text with icons aligned.
 *
 * @param {object} param0 Props for the menu item
 * @returns A menu item component (for use in menus)
 */
export const MenuItem = forwardRef(
  (
    {
      className = '',
      title = '',
      subtitle = '',
      icon = '',
      disabled = false,
      path,
      pathType = LinkType.INTERNAL,
      onClick,
      selected,
      titleClassName = '',
      ...rest
    }: MenuItemProps,
    forwardedRef: any,
  ) => {
    const itemContent = (
      <>
        {!!icon && (
          <Icon
            name={icon}
            className={iconStyles({ disabled, selected })}
            size={IconSize.MD}
          />
        )}

        <div>
          <h6
            className={`${titleStyles({ disabled, selected })} ${titleClassName}`}
          >
            {title}
          </h6>
          {!!subtitle && <p className={subtitleStyles(disabled)}>{subtitle}</p>}
        </div>
      </>
    )

    const testIdSuffix = title.toLowerCase().replace(/ /g, '_')

    return (
      <li
        className={`${itemStyles} ${className}`}
        data-testid={`info_${testIdSuffix}`}
        ref={forwardedRef}
      >
        <Item asChild>
          {path ? (
            <Link
              path={path}
              type={pathType}
              className={linkStyles({ disabled, selected })}
              disabled={disabled}
              onClick={onClick}
              testId={`link_${testIdSuffix}`}
              {...rest}
            >
              {itemContent}
            </Link>
          ) : (
            // Have to use a basic button because the Button component does not accept multi lines of text
            <button
              onClick={onClick}
              className={buttonStyles({ disabled, selected })}
              data-testid={`btn_${testIdSuffix}`}
              {...rest}
            >
              {itemContent}
            </button>
          )}
        </Item>
      </li>
    )
  },
)
